import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, Tab, Input, Button } from "barte-design-system";
import { PlusIcon, TrashIcon, CheckIcon } from "@phosphor-icons/react";
import { Skeleton } from "@shared/components";

import type { RepassTabsProps } from "./repass-tabs.type";
import styles from "./repass-tabs.module.css";

export const RepassTabs = ({
    delays,
    activeDelay,
    isLoading,
    onDelayChange,
    onAddDelay,
    onDeleteDelay
}: RepassTabsProps) => {
    const { t } = useTranslation();
    const [isAddingDelay, setIsAddingDelay] = useState(false);
    const [newDelay, setNewDelay] = useState("");

    const sortedDelays = [...delays].sort((a, b) => {
        if (a === "default") return -1;
        if (b === "default") return 1;
        return Number(a) - Number(b);
    });

    const handleConfirmAddDelay = () => {
        if (!newDelay.trim()) return;
        onAddDelay(newDelay.trim());
        setNewDelay("");
        setIsAddingDelay(false);
    };

    if (isLoading) {
        return (
            <div className={styles["repass-container"]}>
                <Skeleton width="4.5rem" height="1.2rem" />
                <div className={styles['skeleton-tabs']}>
                    <Skeleton width="4rem" height="2rem" />
                    <Skeleton width="4rem" height="2rem" />
                    <Skeleton width="4rem" height="2rem" />
                </div>
            </div>
        );
    }

    return (
        <div className={styles["repass-container"]}>
            <span className={styles.label}>{t("backoffice.pageModalCompanyPlanRepassTimeLabel")}</span>
            <Tabs>
                {sortedDelays.map((delay) => (
                    <Tab
                        key={delay}
                        active={activeDelay === delay}
                        onClick={() => {
                            onDelayChange(delay);
                            setIsAddingDelay(false);
                        }}
                    >
                        <div className={styles['tab-content']}>
                            {delay === "default" ? t("backoffice.pageModalCompanyPlanDelayDefault") : `D+${delay}`}

                            <TrashIcon
                                size={14}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteDelay(delay);
                                }}
                            />
                        </div>
                    </Tab>
                ))}

                {isAddingDelay ? (
                    <div className={styles['add-delay-wrapper']}>
                        <Input
                            type="text"
                            className={styles['delay-input']}
                            value={newDelay}
                            onChange={(e) => setNewDelay(e.target.value)}
                            placeholder={t("backoffice.pageModalCompanyPlanAddDelayPlaceholder")}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleConfirmAddDelay();
                                }
                            }}
                        />
                        <Button
                            className={styles['add-delay-btn']}
                            onClick={handleConfirmAddDelay}
                            disabled={!newDelay.trim()}
                        >
                            <CheckIcon size={16} />
                        </Button>
                    </div>
                ) : (
                    <Tab key="add" active={activeDelay === "add"} onClick={() => setIsAddingDelay(true)}>
                        <PlusIcon size={14} />
                    </Tab>
                )}
            </Tabs>
        </div>
    );
};
